/**
 * Trainer Types
 *
 * Types for managing trainer-client relationships including:
 * - Requesting a trainer
 * - Listing clients (for trainers)
 * - Accepting / declining client requests
 * - Ending trainer-client relationships
 *
 * ## List vs. detail
 *
 * The roster list and the client view page are deliberately **separate shapes**:
 *
 * - {@link TrainerClient} — one row of `GET /api/trainer/clients`, typed from
 *   the shipped `ClientCollection` response.
 * - {@link TrainerClientDetail} — the client view page. Provisional: that page
 *   is not built yet, and its payload will not be a bigger `TrainerClient`.
 *
 * They shared one interface previously, which forced the list to carry fields
 * it never renders and left the detail nowhere to put the ones it needs.
 *
 * @module types/domain/trainer
 */

import type { LockedFeature } from './analytics.types';
import type { UserProfile } from './profile.types';
import type { ID } from '../common/id.types';
import type { Currency } from '../user-preferences.types';

// =============================================================================
// TYPE ALIASES
// =============================================================================

/**
 * Action taken on a pending client request.
 */
export type TrainerRequestAction = 'accept' | 'decline';

/**
 * Valid trainer request actions array for validation.
 */
export const TRAINER_REQUEST_ACTIONS: readonly TrainerRequestAction[] = [
  'accept',
  'decline',
] as const;

/**
 * Status of a trainer-client relationship.
 *
 * Mirrors `TrainerClientStatus::label()`. `ClientCollection::getClientStatus()`
 * collapses `Blocked` into `Pending`, so those three reach the client.
 *
 * `Expired` is an ended contract. Ending is an UPDATE, not a delete — the row
 * survives with `ends_at` and `ended_by` set — so a trainer's roster payload
 * accumulates every engagement they have ever had, not just the live ones.
 * Anything reading this list must therefore choose a status explicitly:
 * "not pending" no longer means "on the books".
 */
export type TrainerClientStatus = 'Accepted' | 'Pending' | 'Expired';

/**
 * Valid trainer client statuses array for validation.
 */
export const TRAINER_CLIENT_STATUSES: readonly TrainerClientStatus[] = [
  'Accepted',
  'Pending',
  'Expired',
] as const;

// =============================================================================
// SCORING VOCABULARY
// =============================================================================

/**
 * Where a coaching score sits — mirrors `App\Enums\ScoreBand`.
 *
 * `unrated` is the absence of a score, not a verdict. There is no coverage
 * floor: any scored week carries a real band and ranks on its value, however
 * thin — so read {@link TrainerClientScored.coverage} to say when it is thin.
 * (`provisional` and the `comparable` flag were retired with the floor.)
 */
export type TrainerScoreBand =
  'thriving' | 'steady' | 'slipping' | 'at_risk' | 'unrated';

/**
 * Score bands in order, best to worst, followed by the non-verdict.
 */
export const TRAINER_SCORE_BANDS: readonly TrainerScoreBand[] = [
  'thriving',
  'steady',
  'slipping',
  'at_risk',
  'unrated',
] as const;

/**
 * The two equally weighted components behind a coaching score.
 *
 * `consistency` is schedule adherence, `goal_pace` is the week's goal movement
 * against the week's expectation. Momentum is reported beside them but is not
 * scored — see {@link TrainerMomentumReport}.
 */
export type TrainerScoreFactorKey = 'consistency' | 'goal_pace';

/**
 * Valid factor keys.
 */
export const TRAINER_SCORE_FACTOR_KEYS: readonly TrainerScoreFactorKey[] = [
  'consistency',
  'goal_pace',
] as const;

/**
 * What the consistency component measured against.
 *
 * `schedule` — a plan schedule governed the week, so this is real programme
 * adherence. `target` — no schedule, so a count-based weekly target was used.
 * Empty string when nothing was rateable.
 */
export type TrainerAdherenceBasis = 'schedule' | 'target' | '';

/**
 * Where a count-based weekly target came from — mirrors
 * `App\Enums\WeeklyTargetSource`.
 *
 * `fallback` means nobody set one and the resolver assumed. The scorer treats
 * that as unrated rather than scoring against an assumption, so a rated
 * `target`-basis factor never carries it — but it can appear on an unrated one.
 */
export type TrainerTargetSource =
  'override' | 'preference' | 'questionnaire' | 'fallback';

/**
 * Whose programming a figure reflects, resolved against the reading trainer —
 * mirrors `App\Enums\PlanAuthorship`.
 *
 * The score is stored per client and cannot know who is asking, so the author
 * travels with it and the API resolves it per viewer. `other_trainer` matters:
 * a client can hold plans and goals from someone else entirely.
 */
export type TrainerPlanAuthorship =
  'prescribed' | 'self_directed' | 'other_trainer';

/**
 * Valid authorship values.
 */
export const TRAINER_PLAN_AUTHORSHIPS: readonly TrainerPlanAuthorship[] = [
  'prescribed',
  'self_directed',
  'other_trainer',
] as const;

/**
 * Why a whole component was withheld — the `reason` on an unrated factor.
 *
 * Distinct from {@link TrainerGoalWithheldReason}, which explains one goal
 * inside a rated `goal_pace`. These explain the absence of the component
 * itself.
 *
 * - `no_stated_target` — nobody set a weekly target, so consistency had nothing
 *   real to measure against (`WeeklyAdherence.php:47`). The resolver's own
 *   fallback is deliberately not scored; this is the actionable one, and it
 *   means "agree a target".
 * - `nothing_yet_due` — the week has not asked anything of the client yet.
 * - `no_scorable_goals` — no active non-frequency goals exist.
 * - `nothing_scorable_this_week` — goals exist, but every one of them withheld.
 * - `no_comparison_week` — no earlier week to measure movement against.
 * - `training_hold` — a hold covered the week. **The commonest of the set**: a
 *   hold spanning seven days withholds every component at once, which is also
 *   the case that leaves the blend null, so this is the reason most likely to
 *   arrive without a score beside it.
 * - `returning_from_medical_hold` — inside the return window after a hold, where
 *   judgement is deliberately not resumed yet.
 * - `insufficient_history` — too little history behind them to read a direction.
 * - `deload_week` — momentum only: a planned deload, so a dip is expected.
 * - `no_training` — momentum only: nothing was trained, so there is no direction.
 *
 * The last three were missing from this union until a backend audit traced the
 * emitting sites. They were not new; they were simply never written down, and
 * consumers mapping only the first five fell through to generic copy on the
 * most common case of all.
 */
export type TrainerFactorReason =
  | 'no_stated_target'
  | 'nothing_yet_due'
  | 'no_scorable_goals'
  | 'nothing_scorable_this_week'
  | 'no_comparison_week'
  | 'training_hold'
  | 'returning_from_medical_hold'
  | 'insufficient_history'
  | 'deload_week'
  | 'no_training';

/**
 * Every documented component-withhold reason.
 */
export const TRAINER_FACTOR_REASONS: readonly TrainerFactorReason[] = [
  'no_stated_target',
  'nothing_yet_due',
  'no_scorable_goals',
  'nothing_scorable_this_week',
  'no_comparison_week',
  'training_hold',
  'returning_from_medical_hold',
  'insufficient_history',
  'deload_week',
  'no_training',
] as const;

/**
 * Why a goal contributed nothing to `goal_pace` this week.
 *
 * Each is a withhold, never a zero — see {@link TrainerGoalPaceFactor.withheld}.
 */
export type TrainerGoalWithheldReason =
  | 'progress_unavailable'
  | 'younger_than_window'
  | 'no_baseline_snapshot'
  | 'no_input_logged'
  | 'no_expected_movement';

// =============================================================================
// SCORE FACTORS
// =============================================================================

/**
 * Fields every factor carries.
 *
 * `score` is null when the component was unrated — data was missing, not bad.
 * Unrated components are dropped from the blend and their weight redistributed,
 * so a null here must never be rendered as a zero.
 */
export interface TrainerScoreFactorBase {
  /** 0–100, or null when unrated */
  score: number | null;
  /** This component's share of the blend, e.g. `0.4` */
  weight: number;
  /**
   * Machine-readable cause when unrated.
   *
   * Widened to `string` on purpose: this is a value the API chooses, and a new
   * one appearing must not be a type error at the boundary. Consumers should
   * map the documented set and degrade gracefully on anything else.
   */
  reason?: TrainerFactorReason | (string & {});
}

/**
 * Schedule adherence, or sessions against a stated weekly target.
 *
 * Which set of fields is populated depends on {@link TrainerConsistencyFactor.basis}:
 * `schedule` carries `scheduled`/`kept`/`missed`, `target` carries
 * `sessions`/`target`/`target_source`.
 */
export interface TrainerConsistencyFactor extends TrainerScoreFactorBase {
  /** What the figure was measured against */
  basis?: TrainerAdherenceBasis;
  /** Prescribed sessions in the week — `schedule` basis */
  scheduled?: number;
  /** Prescribed sessions completed — `schedule` basis */
  kept?: number;
  /** Prescribed sessions not completed — `schedule` basis */
  missed?: number;
  /** Distinct days trained — `target` basis */
  sessions?: number;
  /** The weekly session target — `target` basis */
  target?: number;
  /** Where that target came from — `target` basis */
  target_source?: TrainerTargetSource;
}

/**
 * The week's goal movement against the week's expectation.
 *
 * `on_track` is cumulative standing reported *beside* the score, not folded
 * into it — "did they move this week" and "will this goal land" are different
 * questions and the roster shows both.
 */
export interface TrainerGoalPaceFactor extends TrainerScoreFactorBase {
  /** Active goals excluding frequency goals, which consistency already scores */
  active_goals?: number;
  /** How many of those actually produced a score this week */
  scored_goals?: number;
  /** How many are on track cumulatively — a flag, not a score input */
  on_track?: number;
  /**
   * Counts per withhold reason; goals are never identified by id.
   *
   * A **sparse map of exceptions**, so the field is omitted when there are
   * none — never sent as an empty collection. A rated `goal_pace` implies the
   * withholds were computed, so absence here reads as "nothing to report",
   * never as "not calculated".
   *
   * Read it through `Object.values(withheld ?? {})`. Do not test it for
   * truthiness: an empty object and an empty array are both truthy, so only
   * omission makes that check correct, and only this convention guarantees
   * omission.
   *
   * Deliberately unlike {@link TrainerClientScored.goal_locks}, which is a
   * complete partition over a closed set and does send its zeros.
   */
  withheld?: Partial<Record<TrainerGoalWithheldReason, number>>;
}

/**
 * Direction of travel — volume and training load against the client's
 * **usual** week. Reported beside the score, never folded into it: it carries
 * no `score` and no `weight`, so it renders as a flag, not a bar.
 *
 * "Usual" is the mean of the client's last four comparable weeks, not last
 * week. Percentages are signed and unbounded, so clamp before charting.
 */
export interface TrainerMomentumReport {
  /**
   * Whether the week fell more than 1.5 SD below usual. Null when it could not
   * be judged — {@link TrainerMomentumReport.reason} says why.
   */
  dropping: boolean | null;
  /** Signed percentage change in volume against the usual week */
  volume_change?: number;
  /** Signed percentage change in training load against the usual week */
  training_load_change?: number;
  /** Days since the last session, measured at the scored week's end */
  days_inactive?: number;
  /**
   * Why `dropping` is null: `deload_week`, `no_training`,
   * `insufficient_history`, `training_hold` or `returning_from_medical_hold`.
   * Widened to `string` for the same reason as on
   * {@link TrainerScoreFactorBase.reason}.
   */
  reason?: TrainerFactorReason | (string & {});
}

/**
 * Every factor, keyed. A null means the component was not rated (or, for
 * momentum, not reported) this week.
 */
export interface TrainerScoreFactors {
  consistency: TrainerConsistencyFactor | null;
  goal_pace: TrainerGoalPaceFactor | null;
  momentum: TrainerMomentumReport | null;
}

/**
 * How much of the picture the score rests on.
 *
 * The blend renormalises over whatever was rateable, so without this a client
 * scored on one component is indistinguishable from one scored on both — and
 * the roster ranks them against each other.
 */
export interface TrainerScoreCoverage {
  /** Components that carried a score */
  rated: number;
  /** Components attempted — 2 since the momentum component stopped scoring */
  total: number;
  /** Share of total weight that was rateable; null when nothing was */
  weight: number | null;
}

/**
 * One week of the score's history, oldest first.
 *
 * Both fields are nullable: rows predating the scoring cutover carry no
 * `week_start`, and a week that withheld carries no score. Plot with gaps
 * rather than interpolating.
 */
export interface TrainerScoreHistoryPoint {
  week_start: string | null;
  score: number | null;
}

// =============================================================================
// THE SCORE
// =============================================================================

/**
 * Locked goals per locker, relative to the trainer reading.
 *
 * A lock is a coaching act — it stops the client editing or cancelling the
 * goal. It says nothing about who *set* the goal: goal creation is owner-only,
 * so every goal counted here was set by the client themselves.
 */
export interface TrainerGoalLocks {
  /** Locked by the trainer reading */
  you: number;
  /** Locked by any other coach */
  other: number;
}

/**
 * A client with a coaching score for the last complete week.
 */
export interface TrainerClientScored {
  /** Discriminant — see {@link TrainerClientScore} */
  scored: true;
  /** 0–100 for the last complete week */
  score: number;
  /** The prior scored week, or null when there is none to compare */
  previous_score: number | null;
  /** Band derived from the score */
  band: TrainerScoreBand;
  /** What the score rests on — say so when `rated < total` */
  coverage: TrainerScoreCoverage;
  /** What consistency measured against */
  basis: TrainerAdherenceBasis;
  /** Whose plan the adherence figure reflects; null when there was no schedule */
  plan_authorship: TrainerPlanAuthorship | null;
  /**
   * @deprecated Read {@link TrainerClientScored.goal_locks} instead.
   *
   * Named for authorship, but built by grouping on `user_goals.locked_by`, so
   * `prescribed` means "this trainer locked it" and `self_directed` means
   * "nobody locked it". Neither says who set the goal — and nobody but the
   * client ever does, so all three of its labels are wrong about what they
   * count. Still on the wire until the API retires it; nothing reads it.
   */
  goal_authorship: Partial<Record<TrainerPlanAuthorship, number>> | null;
  /**
   * How many of the client's active goals are LOCKED, and by whom. Null when
   * the client has no active goals at all.
   *
   * Only the locked buckets are counted — unlocked is the default state and a
   * count of it carries no signal. Which is why `{ you: 0, other: 0 }` is
   * reachable and means something different from `null`: "goals, none locked"
   * is a distinct fact from "no goals".
   *
   * **Shares no denominator with `factors.goal_pace.active_goals`.** That
   * figure excludes workout-frequency goals, since consistency already scores
   * that behaviour; these counts deliberately include them, because locking a
   * goal is a coaching act whether or not the goal drives goal_pace. The two
   * must never be subtracted from one another to infer the unlocked count.
   */
  goal_locks: TrainerGoalLocks | null;
  /** Weekly scores, oldest first */
  history: TrainerScoreHistoryPoint[];
  /**
   * The component dragging hardest, or the strongest when nothing drags.
   *
   * `momentum` still arrives on weeks scored before the revamp (stored rows
   * are not re-scored), but momentum is no longer a scored factor — so read it
   * through `getDominantFactor` in the roster model, which ignores it.
   */
  dominant_factor: TrainerScoreFactorKey | 'momentum' | '';
  /** The components behind the score */
  factors: TrainerScoreFactors;
  /** What the trainer should do about it; empty string when there is nothing */
  suggested_action: string;
}

/**
 * A client with nothing to score yet.
 *
 * Not a failure: no plan schedule, no active goals and no comparison week
 * leaves nothing to judge. Rendering this as a zero would sit every new client
 * at the bottom of the roster beside the ones genuinely slipping.
 */
export interface TrainerClientUnscored {
  /** Discriminant — see {@link TrainerClientScore} */
  scored: false;
  score: null;
  previous_score: null;
  band: 'unrated';
  coverage: TrainerScoreCoverage;
  basis: '';
  plan_authorship: null;
  /**
   * @deprecated Read {@link TrainerClientUnscored.goal_locks} instead.
   *
   * Null on this branch by construction: the API declined to extend a key it
   * is retiring to a surface it never had.
   */
  goal_authorship: null;
  /**
   * **Populated here, unlike its deprecated neighbour** — so it is nullable,
   * not the literal `null` the rest of this branch would suggest.
   *
   * A week that could not be scored still knows what is locked: the scorer
   * runs goal pace whatever happens, and every withholding branch carries the
   * lock map through. The goals module reads this beside the per-goal rows.
   *
   * Same trap as {@link TrainerClientUnscored.factors}: typing an absence the
   * API merely *used* to discard as a guarantee leaves a consumer unable to
   * ask. Locks on frequency goals appear only once a week has been re-scored,
   * since stored rows keep whatever map they were written with.
   */
  goal_locks: TrainerGoalLocks | null;
  /** History still renders — earlier weeks may have scored */
  history: TrainerScoreHistoryPoint[];
  dominant_factor: '';
  /**
   * The components, which **may carry a reason even though nothing scored**.
   *
   * Previously typed as literal nulls, which made the commonest unscored week
   * unrepresentable: a training hold covering seven days withholds every
   * component at once, so the blend is null while every
   * component still records *why* it withheld. The API discarded that detail
   * and the type encoded the discarding as a guarantee.
   *
   * Still all-null when there is no scored week at all. So read a reason
   * through the optional chain and treat its absence as "not reported",
   * never as "nothing was wrong".
   *
   * Every scored component here has `score: null` by construction — this
   * branch is the absence of a blend, so a component that scored could not be
   * in it.
   */
  factors: TrainerScoreFactors;
  suggested_action: '';
}

/**
 * A client's coaching score, or the documented absence of one.
 *
 * Discriminated on `scored`. Both branches carry every key — the API returns a
 * uniform shape — so narrow on `scored` rather than probing for a field.
 */
export type TrainerClientScore = TrainerClientScored | TrainerClientUnscored;

// =============================================================================
// LIST SHAPE — GET /api/trainer/clients
// =============================================================================

/**
 * One row of the trainer's roster.
 *
 * This is the LIST shape. The client view page uses {@link TrainerClientDetail}.
 *
 * `username` is the only field guaranteed present and meaningful — `name` is
 * nullable on users and `note` is nullable on the pivot — so identity keys on
 * the handle.
 *
 * @example
 * ```typescript
 * const client: TrainerClient = {
 *   name: 'Wren Castellanos',
 *   username: 'wren.c',
 *   avatar: 'https://example.com/avatars/wren.png',
 *   status: 'Accepted',
 *   note: null,
 *   requested_at: '2026-03-02T10:00:00.000000Z',
 *   plan_name: 'Kettlebell conditioning',
 *   last_workout: '2026-07-26T18:30:00.000000Z',
 *   score: { scored: false, ... },
 * };
 * ```
 */
export interface TrainerClient {
  /** Display name; null when the client never set one */
  name: string | null;
  /** Unique username — the identity field */
  username: string;
  /** Avatar URL */
  avatar: string;
  /** Relationship status */
  status: TrainerClientStatus;
  /**
   * Message sent with the original request; null when none.
   *
   * Survives acceptance — `acceptClientRequest` only updates status — so it
   * doubles as a record of why the client came to this trainer. The column is
   * `text` with no length cap on the validator, so clamp on display.
   */
  note: string | null;
  /** ISO timestamp of the request; null when the pivot has none */
  requested_at: string | null;
  /** Name of this trainer's active plan for the client; null when none */
  plan_name: string | null;
  /** ISO timestamp of the most recent completed workout; null if never */
  last_workout: string | null;
  /** Coaching score for the last complete week */
  score: TrainerClientScore;
  /**
   * How this trainer last reached the client; null when they never have.
   *
   * `ClientCollection::contact()` has always sent this — it shares
   * `ClientContactPresenter` with the attention feed precisely so the two
   * surfaces cannot disagree about whether a client is ignoring their coach —
   * but the type omitted it, so every reader had to cast to see it.
   */
  contact: TrainerClientContact | null;
  /**
   * The hold this trainer has declared, if one is live; null otherwise.
   *
   * Same omission as {@link TrainerClient.contact}, and the one that mattered:
   * without it the roster reads a resting client as off-track, because the
   * score beside them is already unrated for a fully-held week and an unrated
   * score with no reason showing looks like missing data rather than a
   * decision the trainer made.
   */
  hold: TrainerClientHold | null;
}

/**
 * Query parameters accepted by `GET /api/trainer/clients`.
 *
 * Unrecognised sort keys fall through to the default rather than reaching the
 * query, so an invalid value is safe but silent.
 */
export interface TrainerClientQuery {
  /** Matches name or username */
  search?: string;
  /** Whitelisted server-side */
  sort?: TrainerClientSort;
  direction?: 'asc' | 'desc';
  page?: number;
  /** `'false'` returns the full roster unpaginated */
  paginate?: 'true' | 'false';
}

/**
 * Sortable columns — mirrors `ClientManager::SORTABLE`.
 */
export type TrainerClientSort =
  | 'name'
  | 'username'
  | 'score'
  | 'points'
  | 'last_workout'
  | 'requested_at'
  | 'status';

/**
 * Valid sort keys.
 */
export const TRAINER_CLIENT_SORTS: readonly TrainerClientSort[] = [
  'name',
  'username',
  'score',
  'points',
  'last_workout',
  'requested_at',
  'status',
] as const;

// =============================================================================
// DIRECTORY SHAPE — GET /api/trainer
// =============================================================================

/**
 * One trainer as the **client-facing directory** returns them.
 *
 * Deliberately not a {@link TrainerClient}: that is a row of a trainer's own
 * roster and carries a coaching score. This is the other direction entirely —
 * what a client browsing for a trainer is allowed to know — and the endpoint
 * returns exactly nine fields, mapped by hand in `ClientManager::getTrainers()`.
 *
 * The list is **unpaginated and takes no query parameters**: there is no
 * server-side search, sort or filter, so every one of those is the caller's
 * job over the whole array.
 *
 * Two absences drive most of the UI built on this:
 *
 * - There is no bio, credential, specialty, location or price. `username` and
 *   the rating are the only things that distinguish one row from another.
 * - `public_trainer_profile` is **not** here. A trainer who has hidden their
 *   profile is simply absent from the list — except for their own current and
 *   recent clients, who still receive the row. Nothing in this payload lets a
 *   client tell a hidden trainer from a listed one.
 */
export interface TrainerListing {
  /**
   * Display name, or null.
   *
   * Sent **only** when `has_contract` or `in_grace_period` is true — a client
   * browsing the directory never learns a stranger's real name. `users.name`
   * is independently nullable, so null carries two meanings; those two flags
   * are what tell them apart.
   */
  name: string | null;
  /** The handle. Never null, and the only field a directory search can match. */
  username: string;
  /** Deterministic robohash URL derived from the trainer's email. */
  avatar: string;
  /**
   * Mean `satisfaction_score` across every contract this trainer has held,
   * rounded to one decimal — or null when nobody has rated them.
   *
   * Carries no minimum: a single enthusiastic 10 arrives as `10`. Read it with
   * {@link TrainerListing.ratings} or not at all.
   */
  rating: number | null;
  /**
   * The server's own word for {@link TrainerListing.rating} — mirrors
   * `ratingLabel()`. `'No Rating'` when there is no rating.
   *
   * Render it; do not recompute it. The thresholds are uneven (1–4 Below
   * Average, 5–6 Average, 7–8 Good, 9–10 Excellent) and a second
   * implementation on this side would drift from the one that produced it.
   */
  rating_label: string;
  /** How many ratings the mean rests on. Zero when unrated. */
  ratings: number;
  /** This client's trainer right now — accepted and in force, never pending. */
  has_contract: boolean;
  /**
   * A contract with this client that ended inside the last
   * `TrainerClient::GRACE_DAYS` (14).
   *
   * Mutually exclusive with {@link TrainerListing.has_contract} by
   * construction — grace begins where the term ends — so a row is flagged by
   * one or the other and never both. While it is set the client may still rate
   * the trainer, and may hire them again.
   */
  in_grace_period: boolean;
  /**
   * Whether a request would be accepted, from the trainer's
   * `accepting_new_clients` preference.
   *
   * Independent of listing: closing your books does **not** delist you, and
   * hiding your profile does not close your books. `POST /api/trainer` enforces
   * this one and rejects with "Trainer is not accepting new clients".
   */
  accepting_new_clients: boolean;
}

// =============================================================================
// DETAIL SHAPE — the client view page
// =============================================================================

/**
 * The trainer-client relationship itself, independent of the client's profile.
 *
 * Sourced from the `trainer_client` pivot rather than from `users`.
 */
export interface TrainerClientRelationship {
  username: string;
  status: TrainerClientStatus;
  note: string | null;
  requested_at: string | null;
}

/**
 * A single client as seen on the client view page (`/client/:username`).
 *
 * Two reads: `client` is `GET /api/trainer/clients/{username}`
 * ({@link TrainerClientRead}) and `profile` is `GET /api/profile/{username}`,
 * relationship-gated with no subscription check (goals, recent sessions, the
 * live session). They stay composed rather than flattened because they come
 * from different endpoints under different access rules.
 *
 * **Three time bases, and the page must label them.** `client.score` is the
 * last complete week; `client.session_dates` is the last 90 days; the hold,
 * contact and proposals are live and rolling. A client who stopped three days
 * ago still has a fully pre-stop scored week, so `96%` can honestly sit beside
 * "no session in 3 days".
 */
export interface TrainerClientDetail {
  relationship: TrainerClientRelationship;
  profile: UserProfile;
  /** This client, as their trainer reads them. */
  client: TrainerClientRead;
}

/** The blocks of {@link TrainerClientRead} a plan can withhold. */
export type TrainerClientLockedKey = 'session_dates' | 'alerts';

/**
 * Why a block reads empty: one entry per withheld block, absent when nothing
 * is withheld. An empty `alerts` WITH an entry means "not on your plan"; an
 * empty one without means "no open alerts".
 */
export type TrainerClientLockedFeatures = Partial<
  Record<TrainerClientLockedKey, LockedFeature>
>;

/**
 * `GET /api/trainer/clients/{username}` — one client on this trainer's roster.
 *
 * Needs only the trainer role, is not logged as a coaching-feed view, and
 * computes just this client. A 404 means the username is not on the roster
 * (blocked clients included). Starts with the roster row's fields, unchanged,
 * and adds what the coaching feed used to supply for one client.
 *
 * **Only the monitoring blocks are gated** (`client_progress_monitoring`,
 * Premium): `session_dates` and `alerts` read `[]` without it, and
 * `locked_features` names them. Every other key reads the same for every
 * trainer.
 *
 * **Without a live contract** (pending, expired, or a term that has run out)
 * `plan_id`, `active_plan_*`, `billing`, `proposals`, `unprogrammed_since` and
 * `last_plan_change_at` are null and both arrays are empty, on every plan. Keys
 * are never omitted.
 */
export interface TrainerClientRead extends TrainerClient {
  accepted_at: string | null;
  ends_at: string | null;
  ended_by: 'client' | 'trainer' | null;
  /**
   * THIS trainer's own active plan for the client — the one `plan_name` names.
   * What the plan-proposal editor opens on.
   */
  plan_id: string | null;
  /** The plan the client is actually on, whoever wrote it. */
  active_plan_name: string | null;
  /** Whose plan `active_plan_name` is, relative to this trainer. */
  active_plan_authorship: TrainerPlanAuthorship | null;
  /**
   * Guard on the block, never on `fee`: a `fee` of 0 is a client coached pro
   * bono. Null also when the contract has no fee or cycle set.
   */
  billing: TrainerClientBilling | null;
  /** Days trained in the last 90, newest first, one per day (YYYY-MM-DD). */
  session_dates: string[];
  alerts: ClientRiskAlert[];
  /**
   * This trainer's latest proposal per subject, in any status — read
   * `status` before treating a slot as pending.
   */
  proposals: AttentionProposals | null;
  /** Set only while the client has no active plan. */
  unprogrammed_since: string | null;
  /** The active plan's last update. */
  last_plan_change_at: string | null;
  locked_features?: TrainerClientLockedFeatures;
}

// =============================================================================
// COACHING ATTENTION FEED — GET /api/trainer/client-risk-alerts
// =============================================================================

/**
 * The risk signals that can flag a client — mirrors `App\Enums\ClientRiskType`.
 *
 * Note `training_load_drop` keeps its prefix where the others do not; the wire
 * value is not guessable from the label. All five freeze once shipped, because
 * they select the copy the card renders.
 *
 * `acwr_risk` is **legacy-only from 2026-09-08**: the backend no longer raises it
 * (the ratio was refuted — evidence audit §3.1), but persisted rows keep
 * rendering, resolving and being excused by holds, so it stays in the union.
 */
export type ClientRiskType =
  | 'inactivity'
  | 'volume_drop'
  | 'training_load_drop'
  | 'goal_off_track'
  | 'acwr_risk';

export const CLIENT_RISK_TYPES: readonly ClientRiskType[] = [
  'inactivity',
  'volume_drop',
  'training_load_drop',
  'goal_off_track',
  'acwr_risk',
] as const;

/**
 * How a trainer last reached a client. All four are one-way: there is no
 * messaging system, and a client receives these read-only.
 */
export type TrainerMessageKind =
  'message' | 'bump' | 'celebration' | 'share_invite';

export const TRAINER_MESSAGE_KINDS: readonly TrainerMessageKind[] = [
  'message',
  'bump',
  'celebration',
  'share_invite',
] as const;

/**
 * Why a bump was sent. A subset of {@link ClientRiskType} — `acwr_risk` has no
 * bump reason, which is the carve-out that stops the product urging a coach to
 * push a client already in the injury-risk zone.
 */
export type TrainerBumpReason =
  'inactivity' | 'goal_off_track' | 'volume_drop' | 'training_load_drop';

export const TRAINER_BUMP_REASONS: readonly TrainerBumpReason[] = [
  'inactivity',
  'goal_off_track',
  'volume_drop',
  'training_load_drop',
] as const;

/**
 * Why a trainer declared a hold.
 *
 * **Never render these values.** `post_op`, `injury` and `illness` are health
 * information about a client; the server supplies {@link TrainerClientHold.label}
 * so the vocabulary can grow without a frontend release. Keep the union for
 * logic, and do not log either field.
 */
export type TrainingHoldReason =
  | 'post_op'
  | 'injury'
  | 'illness'
  | 'travel'
  | 'personal'
  | 'deload'
  | 'peaking_block'
  | 'intentional_overreach';

export const TRAINING_HOLD_REASONS: readonly TrainingHoldReason[] = [
  'post_op',
  'injury',
  'illness',
  'travel',
  'personal',
  'deload',
  'peaking_block',
  'intentional_overreach',
] as const;

/**
 * What a hold suppresses.
 *
 * `rest` is "not training, on purpose" — it suppresses inactivity, output drops
 * and scoring. `load` is "training hard, on purpose" — it suppresses only the
 * ACWR verdict, and the client stays visible and fully measured.
 */
export type TrainingHoldKind = 'rest' | 'load';

export const TRAINING_HOLD_KINDS: readonly TrainingHoldKind[] = [
  'rest',
  'load',
] as const;

/**
 * Where a hold is in its life.
 *
 * `returning` is the window after `expected_end` passes with nothing logged
 * yet. It exists so a hold never resumes judgement silently — a client back
 * from a six-week rest would otherwise reappear at fifty days inactive, a worse
 * accusation than the one the hold was protecting them from. A closed hold is
 * serialised as `null`, so neither state reaches the card.
 */
export type TrainingHoldState = 'active' | 'returning';

export const TRAINING_HOLD_STATES: readonly TrainingHoldState[] = [
  'active',
  'returning',
] as const;

/** Contract billing cadence — mirrors `App\Enums\TrainerBillingCycle`. */
export type TrainerBillingCycle =
  'bi-weekly' | 'monthly' | 'quarterly' | 'annual';

export const TRAINER_BILLING_CYCLES: readonly TrainerBillingCycle[] = [
  'bi-weekly',
  'monthly',
  'quarterly',
  'annual',
] as const;

// -----------------------------------------------------------------------------
// Risk detail blocks — allow-listed per type, server-side
// -----------------------------------------------------------------------------

/**
 * `acwr_risk`. The stored `risk_zone` key is deliberately not published.
 * Legacy-only from 2026-09-08 — see {@link ClientRiskType}.
 */
export interface AcwrRiskDetails {
  acwr: number;
  acute_load: number;
  chronic_load: number;
}

/** `inactivity`. `days_inactive` measures the client; `days_open` measures the alert. */
export interface InactivityDetails {
  days_inactive: number;
  last_workout: string | null;
}

/**
 * `volume_drop` and `training_load_drop` share one shape. The comparison is
 * the client's **usual** week — the mean of `baseline_weeks` recent comparable
 * weeks — not the week before, so say "below their usual".
 */
export interface OutputDropDetails {
  week_start: string;
  current: number;
  /** The usual week the drop is measured against */
  baseline: number;
  /** How many weeks that usual is the mean of */
  baseline_weeks: number;
  drop_percent: number;
}

/** `goal_off_track`. One alert per goal, fingerprinted server-side. */
export interface GoalOffTrackDetails {
  goal: string;
  progress_percentage: number;
  days_remaining: number;
  target_date: string;
  /**
   * Hashid of the goal this alert names, for linking to it.
   *
   * **Optional, not nullable** — the server reads it back out of the alert's
   * fingerprint and omits the key entirely when that fingerprint is missing or
   * does not parse, rather than publishing an id of zero that would encode to a
   * hashid resolving to a 404. So a goal alert can legitimately arrive with
   * nowhere to link to, and a reader must offer no destination rather than a
   * broken one.
   */
  goal_id?: string;
}

export type RiskDetails =
  AcwrRiskDetails | InactivityDetails | OutputDropDetails | GoalOffTrackDetails;

interface ClientRiskAlertBase {
  /** Hashid. Never a number, never sortable. */
  id: string;
  /** Only `medium` and `high` are produced today; widened so a new tier cannot break parsing. */
  severity: 'medium' | 'high' | (string & {});
  opened_at: string;
  /**
   * How long the **alert** has been open — the prototype's `since`. Not how long
   * the client has been away; that is `details.days_inactive`, and the two
   * differ by the signal's own threshold.
   */
  days_open: number;
  /**
   * Whether a bump may be offered. **Server-derived — never compute an
   * equivalent.** It folds together the ACWR carve-out, rest holds, and an alert
   * already answered by an unopened bump. Getting any one wrong puts a "train
   * harder" button on an injury-risk or post-op row.
   */
  bumpable: boolean;
  /** How long the metric physically needs before a coach's change could show. */
  settles_days: number;
}

/**
 * One open risk alert, discriminated on `type` so `details` narrows with it.
 *
 * `opened_details` is the snapshot from when the alert flagged, and is `null` on
 * alerts predating that column — treat as "no direction known", never as zero,
 * or a load ratio of 1.62 renders as having climbed from nothing.
 */
export type ClientRiskAlert =
  | (ClientRiskAlertBase & {
      type: 'acwr_risk';
      details: AcwrRiskDetails;
      opened_details: AcwrRiskDetails | null;
    })
  | (ClientRiskAlertBase & {
      type: 'inactivity';
      details: InactivityDetails;
      opened_details: InactivityDetails | null;
    })
  | (ClientRiskAlertBase & {
      type: 'volume_drop' | 'training_load_drop';
      details: OutputDropDetails;
      opened_details: OutputDropDetails | null;
    })
  | (ClientRiskAlertBase & {
      type: 'goal_off_track';
      details: GoalOffTrackDetails;
      opened_details: GoalOffTrackDetails | null;
    });

/**
 * A coach-declared hold. `null` on the wire once closed, so "never held" and
 * "hold ended" are indistinguishable to the card — deliberately.
 */
export interface TrainerClientHold {
  reason: TrainingHoldReason;
  kind: TrainingHoldKind;
  /** Server-supplied display copy. Render this, never {@link TrainerClientHold.reason}. */
  label: string;
  /** Free text from the trainer. Render verbatim; never log. */
  note: string | null;
  starts_on: string;
  expected_end: string | null;
  state: TrainingHoldState;
  /** Set while `active`. Mutually exclusive with `days_since_end`. */
  days_remaining: number | null;
  /** Set while `returning`. Mutually exclusive with `days_remaining`. */
  days_since_end: number | null;
}

/**
 * The trainer's most recent outreach — one comm, not a history.
 *
 * `null` when they have never written. Contact is **evidence, never a cause**:
 * nothing on the card turns red because of it, and the copy names the channel
 * rather than claiming a relationship, because an in-person coach who trained
 * someone this morning has not "gone quiet" on them.
 */
export interface TrainerClientContact {
  last_sent_at: string;
  last_kind: TrainerMessageKind;
  last_reason: TrainerBumpReason | null;
  last_read_at: string | null;
  /** Consecutive unopened comms. `>= 2` is the "you have tried repeatedly" signal. */
  unopened_streak: number;
}

/**
 * What the contract charges.
 *
 * **`null` means unpriced, not free.** A `fee` of `0` is a client coached pro
 * bono and arrives as a real block — so guard on the block itself, never on
 * `billing?.fee`, which silently drops every pro-bono client.
 */
export interface TrainerClientBilling {
  /** Minor units, as agreed. `0` is pro bono. */
  fee: number;
  cycle: TrainerBillingCycle;
  /**
   * Minor units, normalised across cycles. **Never derive it** — bi-weekly is
   * `× 26 / 12`, not `× 2`.
   */
  monthly_amount: number;
  /**
   * `fee`, printed by the API in the contract's own currency ("CA$300.00", "¥12,000").
   * Minor units alone can't be printed: nothing else says which currency they
   * are, and a contract's need not be the account's.
   */
  fee_label: string;
  /** `monthly_amount`, printed in the contract's own currency. Render this. */
  monthly_label: string;
}

/**
 * One client on the coaching attention feed.
 *
 * **Three time bases in one row, and they must be labelled.** `alerts`, `hold`
 * and `contact` are live and rolling; `sessions` is the last 30 days; `score`,
 * `band`, `adherence` and `volume_change` are the **last complete week**. A
 * client who stopped three days ago has a fully pre-stop week, so a row can
 * honestly read `96%` beside "no session in 3 days" — unlabelled, that reads as
 * a bug.
 */
export interface CoachingAttentionRow {
  username: string;
  name: string | null;
  avatar: string | null;
  plan_name: string | null;
  /**
   * Hashid of the governing plan, for linking to it.
   *
   * Goes null with {@link CoachingAttentionRow.plan_name} and
   * {@link CoachingAttentionRow.last_plan_change_at} — all three derive from
   * the same active plan, so they are absent for exactly the clients who have
   * none, which is the `unprogrammed` set.
   */
  plan_id: string | null;
  plan_authorship: TrainerPlanAuthorship | null;

  /** Always an array; may be empty. Empty means "nothing tripped a threshold", not "healthy". */
  alerts: ClientRiskAlert[];
  hold: TrainerClientHold | null;
  contact: TrainerClientContact | null;
  billing: TrainerClientBilling | null;

  /** Never null — `unrated` is a value, not an absence. Anything else ranks. */
  band: TrainerScoreBand;
  score: number | null;

  /** Last complete week. Nullable: consistency is legitimately unrated sometimes. */
  adherence: number | null;
  volume_change: number | null;
  in_target_percent: number | null;

  /** ISO dates in the last 30 days, newest first. The sparkline. */
  sessions: string[];
  /**
   * Unbounded, unlike {@link CoachingAttentionRow.sessions}. `null` means never
   * trained. This is the only source for "how long since they trained" past the
   * 30-day window — which is exactly where `lapsed` lives.
   */
  last_session_at: string | null;
  last_plan_change_at: string | null;
  /**
   * When this client's programming gap began: the later of the last plan ending
   * and the contract being accepted. Null whenever a plan is governing, and
   * never null when one is not — `plan_name` and `last_plan_change_at` both go
   * null together for these clients, leaving nothing else to measure from.
   */
  unprogrammed_since: string | null;

  /**
   * The most recent change proposal per subject — **not only live ones**. Read
   * {@link AttentionProposal.status} before treating a slot as pending.
   */
  proposals: AttentionProposals;
}

// -----------------------------------------------------------------------------
// Change proposals
// -----------------------------------------------------------------------------

/**
 * The three unions below deliberately break this file's union-array-guard
 * convention: they have no `CHANGE_PROPOSAL_*` array and no `isChangeProposal*`
 * guard, where every other union here has both.
 *
 * The convention exists to back something. An array feeds a Zod enum, a guard
 * parses a value at a boundary — and both only pay for themselves on values the
 * **client** supplies. All three of these are server-owned: `status` is derived
 * per request rather than read off a column, `kind` is fixed by which of the
 * three create routes was called, and `subject` is narrowed from `kind`. None
 * appears in a create payload, so nothing would ever call the guard, and an
 * array written only to satisfy the pattern is a list that can silently fall
 * out of step with the union it copies.
 *
 * If a client ever comes to *send* one of these, add the array and the guard
 * together, at the boundary that sends it.
 */

/**
 * Which record a proposal changes.
 *
 * The frontend's discriminator, and deliberately coarser than
 * {@link ChangeProposalKind}: a plan's workouts and its schedule are both
 * "plan" to a reader, and it is what keys the feed's `proposals` block.
 */
export type ChangeProposalSubject = 'plan' | 'goal';

/**
 * Which form authored a proposal, and what it writes to.
 *
 * Finer than `subject` because the two plan kinds write somewhere else
 * entirely — and because at most one of them may be pending at a time, so this
 * is what says which is blocking the other.
 *
 * **Never sent in a create payload.** The server derives it from the route;
 * there are three create endpoints, one per kind. A hand-written payload type
 * carrying a `kind` the API does not accept is a mistake this file has already
 * made once — see the note under Write payloads.
 */
export type ChangeProposalKind =
  'plan_workouts' | 'plan_schedule' | 'user_goal';

/**
 * Where a proposal is in its life.
 *
 * **Server-derived, not read off a column.** A proposal past its deadline
 * answers `expired` whether or not the sweep has run, so a reader must never
 * re-derive this by comparing {@link ChangeProposalTimings.expires_at} to now —
 * that would disagree with the API about what it will still accept.
 *
 * `rescinded` is the trainer withdrawing. It is distinct from `expired`
 * because the card says something different about each, and because a
 * rescinded proposal can simply be replaced.
 */
export type ChangeProposalStatus =
  'pending' | 'accepted' | 'declined' | 'expired' | 'rescinded';

/** One line of a rendered diff. Display strings, already localised and formatted. */
export interface ChangeProposalDiffRow {
  label: string;
  before: string;
  after: string;
}

/**
 * What changed, as the reader should see it.
 *
 * **Composed per reader.** The same proposal renders "95 kg" to one party and
 * "209 lb" to the other, so a diff is never shared or cached across the trainer
 * and the client.
 */
export interface ChangeProposalDiff {
  /** One line. For a goal proposal this is a pace statement. */
  summary: string;
  changes: ChangeProposalDiffRow[];
}

/**
 * What the client changed after the proposal was written.
 *
 * Same shape as a diff because it is one — it arrives in `meta.drift` on a
 * `RESOURCE_STALE` refusal and renders through the same component.
 */
export type ChangeProposalDrift = ChangeProposalDiff;

/** Either party on a proposal. Both fields nullable — an account may be gone. */
export interface ChangeProposalParty {
  username: string | null;
  name: string | null;
}

/** The clocks every proposal carries, wherever it is read. */
export interface ChangeProposalTimings {
  sent_at: string | null;
  days_since_sent: number | null;
  /** Null until the client opens it — "not opened" and "not answered" differ. */
  read_at: string | null;
  responded_at: string | null;
  /**
   * **The settle clock starts here, never at `sent_at`.** Proposed Monday and
   * accepted Saturday, the metric has had zero days to move rather than six.
   */
  days_since_response: number | null;
  /**
   * Display only. Not always `sent_at + 14d` — a `plan_schedule` proposal dies
   * at its earliest scheduled session — and never the source of truth for
   * whether something is live. That is {@link ChangeProposalStatus}.
   */
  expires_at: string | null;
}

interface ChangeProposalBase extends ChangeProposalTimings {
  id: string;
  subject: ChangeProposalSubject;
  kind: ChangeProposalKind;
  status: ChangeProposalStatus;
  /** The coaching, and the only part that says why. Required when sent. */
  note: string | null;
  /** Null when the plan or goal has been deleted since. */
  subject_name: string | null;
  client: ChangeProposalParty;
}

/**
 * One proposal in full — `GET /api/change-proposal/{id}`.
 *
 * What the client answers against.
 */
export interface ChangeProposal extends ChangeProposalBase {
  /**
   * True for the schedule the app suggests with a newly generated plan: no
   * trainer sent it, so {@link trainer} is null and {@link note} is the app's.
   */
  generated: boolean;
  /** Null when {@link generated}: there is no trainer to name. */
  trainer: ChangeProposalParty | null;
  diff: ChangeProposalDiff;
}

/**
 * One row of the trainer's outbox — `GET /api/trainer/change-proposal`.
 *
 * Deliberately not a partial {@link ChangeProposal}. A list answers "what is
 * outstanding, from whom, since when, and how big", so it carries a flat
 * `diff_summary` and no `changes` at all — shipping the per-exercise breakdown
 * for rows that never render it would multiply the payload, and for a schedule
 * proposal would cost a query per row resolving workout names nothing shows.
 * `trainer` is absent because the list is already scoped to the caller.
 */
export interface ChangeProposalListRow extends ChangeProposalBase {
  diff_summary: string;
}

/**
 * A proposal as the attention card sees it — one slot on a feed row.
 *
 * Carries no diff: the card renders a sentence and a button, never a
 * breakdown, and shipping one per roster row would multiply the feed for data
 * only the review surface uses.
 *
 * **A slot holds the most recent proposal per subject, not only a live one.**
 * Presence does not imply pending — a declined proposal stays here on purpose,
 * because "they declined your plan change 2d ago" is what tells a coach to try
 * something else, and a slot that emptied on the answer would erase the very
 * outcome the card exists to report. Every read branches on `status` first.
 */
export interface AttentionProposal extends ChangeProposalTimings {
  id: string;
  kind: ChangeProposalKind;
  status: ChangeProposalStatus;
  /**
   * Which goal this is about; `null` on a plan proposal.
   *
   * Load-bearing. A client may hold an off-track alert per goal but only one
   * goal proposal across all of them, so without this the card cannot tell
   * "pending on this goal" from "blocked by a proposal on a different one" —
   * and would offer an adjust action that 409s.
   */
  goal_id: string | null;
  /**
   * What the proposal is about — the plan's name or the goal's title.
   *
   * Here for one sentence: a row blocked by a proposal on a *different* goal
   * has to name it, and `goal_id` alone cannot. Without it the card falls back
   * to "another goal", which is true but tells a coach nothing they can act on.
   *
   * Null when the subject has been deleted since, so every read needs a
   * fallback — the proposal outlives the thing it was about.
   */
  subject_name: string | null;
  /**
   * Whether a nudge may be offered about this proposal.
   *
   * **Server-derived, and emphatically not `bumpable`.** That flag folds an
   * ACWR carve-out which inverts here: it is false on an injury-risk row
   * because a bump says "train harder", whereas reminding that same client to
   * accept a *deload* says the opposite. Reusing it would strip the reminder
   * from precisely the rows most likely to carry a proposal.
   */
  remindable: boolean;
}

/** The feed's proposal slots. Both keys always present; either may be `null`. */
export interface AttentionProposals {
  plan: AttentionProposal | null;
  goal: AttentionProposal | null;
}

// -----------------------------------------------------------------------------
// Write payloads
// -----------------------------------------------------------------------------
//
// Deliberately absent. The bump and declare-hold payloads are defined once, as
// Zod schemas in `validation/trainer.schemas.ts`, and their param types are
// inferred from there — `SendBumpSchemaInput` and
// `DeclareTrainingHoldSchemaInput`, matching how ChallengeInput and
// CreateUserExercisePreferenceInput are derived for their services.
//
// Hand-written interfaces sat here briefly and drifted from the server inside
// one commit: they carried a `kind` the API does not accept, and typed the
// required `starts_on` as optional. A second definition of a contract is a
// second thing to keep in sync, and this one lost.
//
// The read shapes above stay hand-written — nothing validates an inbound
// payload, so there is no schema to infer them from.

// =============================================================================
// REQUEST PAYLOADS & RESPONSES
// =============================================================================

/**
 * Payload for requesting a trainer.
 *
 * Sent by the **client**, not the trainer — `POST /api/trainer` is client-only
 * (`abort_unless(hasRole(Role::CLIENT), 403)`), so there is no trainer-side
 * invite.
 */
export interface TrainerRequestPayload {
  /** Username of the trainer being requested */
  username: ID;
  /** Optional message, stored on `trainer_client.note` */
  note?: string | null;
}

/**
 * The terms a trainer sets when accepting a client — required by
 * `StoreTrainerClientContractRequest` on `accept`. Dō records them; it takes
 * no payment.
 */
export interface TrainerContractTerms {
  /**
   * Per-cycle fee in major units (`"120.00"`, `"0.00"` for pro bono). The API
   * takes up to two decimals (`decimal:0,2`); the web form always sends
   * exactly two, so every contract is recorded the same way.
   */
  fee: string;
  cycle: TrainerBillingCycle;
  /** Last day of the contract, `YYYY-MM-DD`. */
  ends_at: string;
  /** The contract's own currency, which need not be the account default. */
  currency: Currency;
}

/**
 * Payload for responding to a client request. Declining is the action alone;
 * accepting carries the contract terms.
 */
export type TrainerRequestActionPayload =
  { action: 'decline' } | ({ action: 'accept' } & TrainerContractTerms);

/**
 * Response from the trainer request endpoint.
 */
export interface TrainerRequestResponse {
  message: string;
  status: string;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a value is a valid TrainerRequestAction.
 */
export function isTrainerRequestAction(
  value: unknown
): value is TrainerRequestAction {
  return (
    typeof value === 'string' &&
    TRAINER_REQUEST_ACTIONS.includes(value as TrainerRequestAction)
  );
}

/**
 * Type guard to check if a value is a valid TrainerClientStatus.
 */
export function isTrainerClientStatus(
  value: unknown
): value is TrainerClientStatus {
  return (
    typeof value === 'string' &&
    TRAINER_CLIENT_STATUSES.includes(value as TrainerClientStatus)
  );
}

/**
 * Type guard to check if a value is a valid TrainerScoreBand.
 */
export function isTrainerScoreBand(value: unknown): value is TrainerScoreBand {
  return (
    typeof value === 'string' &&
    TRAINER_SCORE_BANDS.includes(value as TrainerScoreBand)
  );
}

/**
 * Type guard to check if a value is a valid ClientRiskType.
 */
export function isClientRiskType(value: unknown): value is ClientRiskType {
  return (
    typeof value === 'string' &&
    CLIENT_RISK_TYPES.includes(value as ClientRiskType)
  );
}

/**
 * Type guard to check if a value is a valid TrainerMessageKind.
 */
export function isTrainerMessageKind(
  value: unknown
): value is TrainerMessageKind {
  return (
    typeof value === 'string' &&
    TRAINER_MESSAGE_KINDS.includes(value as TrainerMessageKind)
  );
}

/**
 * Type guard to check if a value is a valid TrainerBumpReason.
 *
 * Note `acwr_risk` is deliberately absent: an injury-risk alert has no bump
 * reason, which is what stops the product urging a coach to push that client.
 */
export function isTrainerBumpReason(
  value: unknown
): value is TrainerBumpReason {
  return (
    typeof value === 'string' &&
    TRAINER_BUMP_REASONS.includes(value as TrainerBumpReason)
  );
}

/**
 * Type guard to check if a value is a valid TrainingHoldKind.
 */
export function isTrainingHoldKind(value: unknown): value is TrainingHoldKind {
  return (
    typeof value === 'string' &&
    TRAINING_HOLD_KINDS.includes(value as TrainingHoldKind)
  );
}

/**
 * Type guard to check if a value is a valid TrainingHoldReason.
 */
export function isTrainingHoldReason(
  value: unknown
): value is TrainingHoldReason {
  return (
    typeof value === 'string' &&
    TRAINING_HOLD_REASONS.includes(value as TrainingHoldReason)
  );
}

/**
 * Type guard to check if a value is a valid TrainingHoldState.
 */
export function isTrainingHoldState(
  value: unknown
): value is TrainingHoldState {
  return (
    typeof value === 'string' &&
    TRAINING_HOLD_STATES.includes(value as TrainingHoldState)
  );
}

/**
 * Type guard to check if a value is a valid TrainerBillingCycle.
 */
export function isTrainerBillingCycle(
  value: unknown
): value is TrainerBillingCycle {
  return (
    typeof value === 'string' &&
    TRAINER_BILLING_CYCLES.includes(value as TrainerBillingCycle)
  );
}

/**
 * Type guard to check if a value is a valid TrainerPlanAuthorship.
 */
export function isTrainerPlanAuthorship(
  value: unknown
): value is TrainerPlanAuthorship {
  return (
    typeof value === 'string' &&
    TRAINER_PLAN_AUTHORSHIPS.includes(value as TrainerPlanAuthorship)
  );
}

/**
 * Type guard to check if a value is a valid TrainerRequestPayload.
 */
export function isTrainerRequestPayload(
  value: unknown
): value is TrainerRequestPayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    'username' in obj &&
    (typeof obj.username === 'string' || typeof obj.username === 'number')
  );
}

/**
 * Type guard to check if a value is a valid TrainerRequestActionPayload.
 */
export function isTrainerRequestActionPayload(
  value: unknown
): value is TrainerRequestActionPayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return 'action' in obj && isTrainerRequestAction(obj.action);
}

/**
 * Type guard to check if a value is a valid TrainerClient.
 */
export function isTrainerClient(value: unknown): value is TrainerClient {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    (obj.name === null || typeof obj.name === 'string') &&
    typeof obj.username === 'string' &&
    typeof obj.avatar === 'string' &&
    isTrainerClientStatus(obj.status) &&
    (obj.note === null || typeof obj.note === 'string') &&
    typeof obj.score === 'object' &&
    obj.score !== null
  );
}

/**
 * Type guard to check if a value is a valid TrainerRequestResponse.
 */
export function isTrainerRequestResponse(
  value: unknown
): value is TrainerRequestResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return typeof obj.message === 'string' && typeof obj.status === 'string';
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Narrows a score to the scored variant.
 *
 * @example
 * ```typescript
 * if (isScored(client.score)) {
 *   render(client.score.band, client.score.score);
 * }
 * ```
 */
export function isScored(
  score: TrainerClientScore
): score is TrainerClientScored {
  return score.scored;
}

/**
 * Change since the previous scored week, or null when there is nothing to
 * compare against.
 */
export function scoreDelta(score: TrainerClientScore): number | null {
  if (!isScored(score) || score.previous_score === null) {
    return null;
  }
  return score.score - score.previous_score;
}

/**
 * Whether the adherence figure reflects programming this trainer set.
 *
 * A `self_directed` or `other_trainer` result is not a problem — it just means
 * the number describes work the reader did not prescribe, which is worth
 * saying out loud beside it.
 */
export function isOwnProgramming(score: TrainerClientScore): boolean {
  return isScored(score) && score.plan_authorship === 'prescribed';
}

/**
 * Gets the display name for a client, falling back to the username.
 *
 * `users.name` is nullable, so this — not `client.name` — is the only safe way
 * to label a client.
 */
export function getClientDisplayName(
  client: Pick<TrainerClient, 'name' | 'username'>
): string {
  return client.name?.trim() || client.username;
}

/**
 * Checks if a client relationship is accepted, narrowing nothing — status is a
 * plain field on a uniform row shape.
 */
export function isClientAccepted(client: TrainerClient): boolean {
  return client.status === 'Accepted';
}

/**
 * Checks if a client relationship is pending.
 */
export function isClientPending(client: TrainerClient): boolean {
  return client.status === 'Pending';
}

/**
 * Checks if a client relationship has ended.
 *
 * An expired client keeps whatever score they held when the contract closed.
 * That score is history, not a reading — it describes a week the trainer was
 * still coaching them. Do not band, rank or average it alongside the live
 * roster.
 */
export function isClientExpired(client: TrainerClient): boolean {
  return client.status === 'Expired';
}

/**
 * Filters clients by their relationship status.
 */
export function filterClientsByStatus(
  clients: TrainerClient[],
  status: TrainerClientStatus
): TrainerClient[] {
  return clients.filter((client) => client.status === status);
}

/**
 * Gets accepted clients from a roster.
 */
export function getAcceptedClients(clients: TrainerClient[]): TrainerClient[] {
  return filterClientsByStatus(clients, 'Accepted');
}

/**
 * Gets pending requests from a roster.
 */
export function getPendingClients(clients: TrainerClient[]): TrainerClient[] {
  return filterClientsByStatus(clients, 'Pending');
}

/**
 * Gets ended engagements from a roster.
 */
export function getExpiredClients(clients: TrainerClient[]): TrainerClient[] {
  return filterClientsByStatus(clients, 'Expired');
}

/**
 * Counts clients by status.
 *
 * Seeded from {@link TRAINER_CLIENT_STATUSES} rather than a literal, so a
 * status added to the union can never again land here as `undefined + 1`.
 */
export function countClientsByStatus(
  clients: TrainerClient[]
): Record<TrainerClientStatus, number> {
  const seed = Object.fromEntries(
    TRAINER_CLIENT_STATUSES.map((status) => [status, 0])
  ) as Record<TrainerClientStatus, number>;

  return clients.reduce((acc, client) => {
    acc[client.status]++;
    return acc;
  }, seed);
}

/**
 * Finds a client by username.
 */
export function findClientByUsername(
  clients: TrainerClient[],
  username: string
): TrainerClient | undefined {
  return clients.find(
    (client) => client.username.toLowerCase() === username.toLowerCase()
  );
}

/**
 * Searches clients by name or username.
 *
 * The endpoint supports `?search=` server-side; this is for filtering an
 * already-loaded roster.
 */
export function searchClients(
  clients: TrainerClient[],
  query: string
): TrainerClient[] {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) {
    return clients;
  }
  return clients.filter(
    (client) =>
      (client.name?.toLowerCase().includes(lowerQuery) ?? false) ||
      client.username.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Sorts clients alphabetically by display name.
 */
export function sortClientsByName(clients: TrainerClient[]): TrainerClient[] {
  return [...clients].sort((a, b) =>
    getClientDisplayName(a)
      .toLowerCase()
      .localeCompare(getClientDisplayName(b).toLowerCase())
  );
}

/**
 * Sorts clients by coaching score, lowest first.
 *
 * Unscored clients sort last rather than worst. An unmeasured client at the
 * top of a triage list is a false alarm.
 */
export function sortClientsByScore(clients: TrainerClient[]): TrainerClient[] {
  return [...clients].sort((a, b) => {
    const left = isScored(a.score) ? a.score.score : Number.POSITIVE_INFINITY;
    const right = isScored(b.score) ? b.score.score : Number.POSITIVE_INFINITY;
    return left - right;
  });
}
